import React, { useState, useEffect } from 'react';
import {
  Progress, Card, Row, Col, Statistic, Tag, Table, Alert,
  Typography, Space, Button, Tooltip, List, Avatar
} from 'antd';
import {
  ClockCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  SyncOutlined, PlayCircleOutlined, StopOutlined
} from '@ant-design/icons';
import batchTaskService from '../../services/batchTaskService';

const { Title, Text, Paragraph } = Typography;

/**
 * 批量任务进度组件
 * 实时展示批量任务的执行进度和详细状态
 */
const BatchTaskProgress = ({ task, progress, onRetryFailed }) => {
  const [taskDetail, setTaskDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  // 获取任务详情
  useEffect(() => {
    if (task?.taskId) {
      fetchTaskDetail();
      
      // 如果任务还在运行中，定时刷新详情
      if (progress?.status === 'RUNNING') {
        const interval = setInterval(() => {
          fetchTaskDetail();
        }, 3000); // 每3秒刷新一次任务详情
        
        return () => clearInterval(interval);
      }
    }
  }, [task?.taskId, progress?.status]);

  // 当进度状态变化时，立即刷新一次任务详情
  useEffect(() => {
    if (task?.taskId && progress?.status && 
        (progress.status === 'COMPLETED' || progress.status === 'FAILED')) {
      // 任务完成时立即获取最新的任务详情
      fetchTaskDetail();
    }
  }, [progress?.status]);

  const fetchTaskDetail = async () => {
    try {
      setLoading(true);
      const response = await batchTaskService.getBatchTaskDetail(task.taskId);
      if (response.success) {
        setTaskDetail(response.data);
      }
    } catch (error) {
      console.error('获取任务详情失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取状态颜色
  const getStatusColor = (status) => {
    return batchTaskService.getTaskStatusColor(status);
  };

  // 获取状态文本
  const getStatusText = (status) => {
    return batchTaskService.formatTaskStatus(status);
  };

  // 获取状态图标
  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return <ClockCircleOutlined />;
      case 'RUNNING':
        return <SyncOutlined spin />;
      case 'COMPLETED':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'FAILED':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  // 任务项表格列定义
  const columns = [
    {
      title: '序号',
      dataIndex: 'itemOrder',
      width: 80,
      sorter: (a, b) => a.itemOrder - b.itemOrder
    },
    {
      title: '门店名称',
      dataIndex: 'storeName',
      width: 200,
      render: (text, record) => (
        <Space>
          {text || `门店${record.itemOrder}`}
        </Space>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: '结果',
      dataIndex: 'resultData',
      width: 200,
      render: (result, record) => {
        if (record.status === 'COMPLETED' && result) {
          return (
            <Space direction="vertical" size="small">
              {result.qrCode && (
                <a 
                  href={result.qrCode} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ fontSize: '12px' }}
                >
                  查看二维码
                </a>
              )}
            </Space>
          );
        }
        
        if (record.status === 'FAILED' && record.errorMessage) {
          return (
            <Tooltip title={record.errorMessage}>
              <Text type="danger" style={{ fontSize: '12px' }}>
                {record.errorMessage.length > 30 
                  ? `${record.errorMessage.substring(0, 30)}...` 
                  : record.errorMessage}
              </Text>
            </Tooltip>
          );
        }
        
        return '-';
      }
    },
    {
      title: '重试次数',
      dataIndex: 'retryCount',
      width: 100,
      render: (count, record) => (
        <Text>
          {count || 0} / {record.maxRetryCount || 3}
        </Text>
      )
    },
    {
      title: '执行时间',
      width: 150,
      render: (_, record) => {
        if (record.completedAt) {
          const duration = batchTaskService.formatTaskDuration(
            record.startedAt, 
            record.completedAt
          );
          return <Text style={{ fontSize: '12px' }}>{duration}</Text>;
        }
        
        if (record.startedAt) {
          const duration = batchTaskService.formatTaskDuration(record.startedAt);
          return <Text style={{ fontSize: '12px' }}>{duration}</Text>;
        }
        
        return '-';
      }
    }
  ];

  if (!progress && !taskDetail) {
    return <div>暂无进度信息</div>;
  }

  const currentProgress = progress || taskDetail?.progress;
  const items = taskDetail?.items || [];

  return (
    <div>
      {/* 任务概览 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Statistic
              title="总任务数"
              value={currentProgress?.totalCount || 0}
              prefix={<PlayCircleOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="已完成"
              value={currentProgress?.completedCount || 0}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="成功"
              value={currentProgress?.successCount || 0}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="失败"
              value={currentProgress?.failedCount || 0}
              valueStyle={{ color: '#cf1322' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Col>
        </Row>
      </Card>

      {/* 进度条 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Title level={5}>执行进度</Title>
        <Progress
          percent={Math.round(currentProgress?.progressPercentage || 0)}
          status={currentProgress?.status === 'FAILED' ? 'exception' : 'active'}
          format={percent => `${percent}%`}
        />
        
        <Row style={{ marginTop: 16 }}>
          <Col span={12}>
            <Text strong>当前阶段：</Text>
            <Text>{currentProgress?.currentPhase || '等待开始'}</Text>
          </Col>
          <Col span={12}>
            <Text strong>预计剩余时间：</Text>
            <Text>
              {currentProgress ? batchTaskService.estimateRemainingTime(currentProgress) : '计算中...'}
            </Text>
          </Col>
        </Row>
      </Card>

      {/* 状态提醒 */}
      {currentProgress?.status === 'RUNNING' && (
        <Alert
          message="任务执行中"
          description="批量创建任务正在后台执行，请耐心等待。页面关闭不会影响任务执行。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {currentProgress?.status === 'COMPLETED' && (
        <Alert
          message="任务执行完成"
          description={`批量创建任务已完成，成功创建 ${currentProgress.successCount} 个活码${
            currentProgress.failedCount > 0 ? `，失败 ${currentProgress.failedCount} 个` : ''
          }。`}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {currentProgress?.status === 'FAILED' && (
        <Alert
          message="任务执行失败"
          description="批量创建任务执行失败，请查看详细错误信息并重试。"
          type="error"
          showIcon
          action={
            currentProgress.failedCount > 0 && onRetryFailed && (
              <Button size="small" danger onClick={onRetryFailed}>
                重试失败项
              </Button>
            )
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 任务项详情表格 */}
      {items.length > 0 && (
        <Card size="small" title="任务项详情">
          <Table
            columns={columns}
            dataSource={items}
            rowKey="id"
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`
            }}
            loading={loading}
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ padding: '8px 24px' }}>
                  {record.itemConfig && (
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>配置信息：</Text>
                      <pre style={{ 
                        background: '#f5f5f5', 
                        padding: 8, 
                        borderRadius: 4,
                        fontSize: '12px',
                        margin: '4px 0',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        wordWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        maxWidth: '100%'
                      }}>
                        {JSON.stringify(record.itemConfig, (key, value) => {
                          // 截断长URL
                          if (typeof value === 'string' && value.length > 100 && 
                              (value.startsWith('http') || key.toLowerCase().includes('url'))) {
                            return value.substring(0, 100) + '...';
                          }
                          return value;
                        }, 2)}
                      </pre>
                    </div>
                  )}
                  
                  {record.resultData && (
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>执行结果：</Text>
                      <pre style={{ 
                        background: '#f6ffed', 
                        padding: 8, 
                        borderRadius: 4,
                        fontSize: '12px',
                        margin: '4px 0',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        wordWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        maxWidth: '100%'
                      }}>
                        {JSON.stringify(record.resultData, (key, value) => {
                          // 截断长URL，特别是二维码URL
                          if (typeof value === 'string' && value.length > 100 && 
                              (value.startsWith('http') || key.toLowerCase().includes('url') || 
                               key.toLowerCase().includes('qrcode'))) {
                            return value.substring(0, 100) + '...';
                          }
                          return value;
                        }, 2)}
                      </pre>
                    </div>
                  )}
                  
                  {record.errorMessage && (
                    <div>
                      <Text strong>错误信息：</Text>
                      <div style={{ 
                        background: '#fff2f0', 
                        padding: 8, 
                        borderRadius: 4,
                        color: '#cf1322',
                        fontSize: '12px',
                        margin: '4px 0',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        wordWrap: 'break-word',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {record.errorMessage}
                      </div>
                    </div>
                  )}
                </div>
              ),
              rowExpandable: (record) => 
                !!(record.itemConfig || record.resultData || record.errorMessage)
            }}
          />
        </Card>
      )}
    </div>
  );
};

export default BatchTaskProgress;